// src/autopilotMode/services/PolicyMatchingService.js
// Service for matching AI-generated controls with specific policy directives

import { createClient } from '@supabase/supabase-js';
import { authFetch } from '../../utils/authFetch';

export class PolicyMatchingService {
  constructor() {
    // Initialize Supabase client
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || process.env.REACT_APP_SUPABASE_DATABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      console.error('[PolicyMatchingService] Missing Supabase configuration');
      throw new Error('Supabase configuration not found');
    }

    this.supabase = createClient(supabaseUrl, supabaseKey, {
      auth: { persistSession: false }
    });
  }

  /**
   * Main entry point: Enrich controls with policy directive evidence
   * @param {Array} controls - AI-generated controls to enrich
   * @param {Object} config - Configuration: { policyTypes, riskNarrative, threshold, topK }
   * @returns {Array} Controls with policyEvidence attached
   */
  async enrichControlsWithPolicies(controls, config = {}) {
    const {
      policyTypes = ['ai-control'],
      threshold = 0.3,
      topK = 3
    } = config;

    console.log(`[PolicyMatchingService] Enriching ${controls.length} controls with policy directives`, {
      policyTypes,
      threshold,
      topK
    });

    const enrichedControls = [];

    for (const control of controls) {
      try {
        // Generate embedding for control text
        const queryText = `${control.name || control.controlName}\n${control.description || ''}`;
        const embedding = await this.generateEmbedding(queryText);

        // Query Supabase for matching policy directives
        const directives = await this.queryPolicyDirectives(embedding, policyTypes, topK * 2); // Get extra to filter

        // Filter by threshold and limit to topK
        const matchedDirectives = directives
          .filter(d => d.similarity >= threshold)
          .slice(0, topK);

        console.log(`[PolicyMatchingService] Control "${control.name || control.controlName}": ${matchedDirectives.length} directives matched (threshold: ${threshold})`);

        enrichedControls.push({
          ...control,
          policyEvidence: matchedDirectives,
          source: matchedDirectives.length > 0 ? 'AI_with_Policy_Match' : 'AI_Generated'
        });

      } catch (error) {
        console.error(`[PolicyMatchingService] Error matching control "${control.name}":`, error);
        // Include control anyway without policy evidence
        enrichedControls.push({
          ...control,
          policyEvidence: [],
          source: 'AI_Generated'
        });
      }
    }

    const matchedCount = enrichedControls.filter(c => c.policyEvidence?.length > 0).length;
    console.log(`[PolicyMatchingService] ✅ Enrichment complete: ${matchedCount}/${controls.length} controls matched with directives`);

    return enrichedControls;
  }

  /**
   * Query Supabase policy tables for matching directives
   * @param {Array} embedding - Query embedding vector
   * @param {Array} policyTypes - Policy types to search: ['governance', 'security', 'ai-control']
   * @param {number} topK - Number of top matches to return
   * @returns {Array} Matching policy directives with similarity scores
   */
  async queryPolicyDirectives(embedding, policyTypes, topK) {
    const tables = {
      'governance': 'policy_governance_embeddings',
      'security': 'policy_security_embeddings',
      'ai-control': 'policy_ai_control_embeddings'
    };

    // Determine which tables to query
    const tablesToQuery = policyTypes.length === 0
      ? Object.entries(tables)
      : Object.entries(tables).filter(([type]) => policyTypes.includes(type));

    console.log(`[PolicyMatchingService] Querying ${tablesToQuery.length} policy tables:`, tablesToQuery.map(([t]) => t));

    let allDirectives = [];

    for (const [type, tableName] of tablesToQuery) {
      try {
        // Fetch all embeddings from table
        const { data, error } = await this.supabase
          .from(tableName)
          .select('text, embedding, file_name, directive_name, section_number, section_title, control_type, regulatory_reference, sub_category');

        if (error) {
          console.warn(`[PolicyMatchingService] Error querying ${tableName}:`, error.message);
          continue;
        }

        if (!data || data.length === 0) {
          console.warn(`[PolicyMatchingService] No data in ${tableName}`);
          continue;
        }

        console.log(`[PolicyMatchingService] Retrieved ${data.length} policy directives from ${tableName}`);

        // Calculate similarity for each directive
        const similarities = data.map(directive => {
          // Parse embedding if it's a PostgreSQL array string (e.g., "[0.1, 0.2, ...]")
          let embeddingVector = directive.embedding;
          if (typeof embeddingVector === 'string') {
            try {
              embeddingVector = JSON.parse(embeddingVector);
            } catch (parseError) {
              console.warn(`[PolicyMatchingService] Failed to parse embedding for directive in ${tableName}:`, parseError.message);
              embeddingVector = null;
            }
          }

          const similarity = this.cosineSimilarity(embedding, embeddingVector);

          return {
            // Structured directive info (fallback to parsing if null)
            directiveName: directive.directive_name || this.parseDirectiveName(directive.text),
            sectionNumber: directive.section_number || 'N/A',
            sectionTitle: directive.section_title || 'N/A',
            controlType: directive.control_type || 'N/A',
            regulatoryReference: directive.regulatory_reference || 'N/A',

            // Full text
            text: directive.text,

            // Metadata
            file_name: directive.file_name,
            policyType: type,
            sub_category: directive.sub_category,

            // Similarity score
            similarity
          };
        });

        allDirectives.push(...similarities);

      } catch (error) {
        console.error(`[PolicyMatchingService] Error processing ${tableName}:`, error);
      }
    }

    // Sort by similarity and return top K
    const topDirectives = allDirectives
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, topK);

    console.log(`[PolicyMatchingService] Returning top ${topDirectives.length} directives (from ${allDirectives.length} total)`);

    return topDirectives;
  }

  /**
   * Fallback parser for directive name when metadata is not available
   * Parses from pipe-delimited text format
   * @param {string} text - Policy text in format: "Name|Section|Type|Description|..."
   * @returns {string} Directive name
   */
  parseDirectiveName(text) {
    if (!text) return 'Unknown Directive';

    const parts = text.split('|');
    return parts[0]?.trim() || 'Unknown Directive';
  }

  /**
   * Calculate cosine similarity between two vectors
   * @param {Array} a - First vector
   * @param {Array} b - Second vector
   * @returns {number} Similarity score (0-1)
   */
  cosineSimilarity(a, b) {
    if (!a || !b || !Array.isArray(a) || !Array.isArray(b)) {
      console.warn('[PolicyMatchingService] Invalid vectors for similarity calculation');
      return 0;
    }

    if (a.length !== b.length) {
      console.warn(`[PolicyMatchingService] Vector length mismatch: ${a.length} vs ${b.length}`);
      return 0;
    }

    let dotProduct = 0;
    let normA = 0;
    let normB = 0;

    for (let i = 0; i < a.length; i++) {
      dotProduct += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }

    if (normA === 0 || normB === 0) {
      return 0;
    }

    return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
  }

  /**
   * Generate embedding for text using Netlify function
   * @param {string} text - Text to embed
   * @returns {Array} Embedding vector
   */
  async generateEmbedding(text) {
    try {
      const response = await authFetch('/.netlify/functions/generateEmbedding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.slice(0, 8000) })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.embedding;

    } catch (error) {
      console.error('[PolicyMatchingService] Embedding generation failed:', error);
      throw error;
    }
  }
}
